<template>
  <div class="blog">
    <!-- Hero Section -->
    <section class="bg-bleumarine text-white py-16">
      <div class="container mx-auto px-4">
        <h1 class="text-3xl md:text-4xl font-bold text-center mb-4">Blog & Tutoriels</h1>
        <p class="text-center text-turquoise max-w-2xl mx-auto">
          Découvrez nos articles techniques, tutoriels et actualités IT pour rester informé des dernières tendances technologiques.
        </p>
      </div>
    </section>

    <!-- Search and Filter -->
    <section class="py-8 bg-white border-b">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row gap-4 items-center justify-between">
          <!-- Search -->
          <div class="w-full md:w-1/2">
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Rechercher un article..."
              class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
            >
          </div>
          <!-- Category Filter -->
          <div class="w-full md:w-1/2">
            <SearchableSelect
              v-model="selectedCategory"
              :options="categoryOptions"
              placeholder="Toutes les catégories"
              search-placeholder="Rechercher une catégorie..."
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Articles Grid -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div v-if="filteredArticles.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <article v-for="article in filteredArticles" :key="article.id" class="card">
            <div class="bg-gray-200 h-48 rounded-lg mb-4 flex items-center justify-center">
              <Newspaper class="w-16 h-16 text-gray-400" :stroke="'#fff'" />
            </div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-turquoise font-medium">{{ article.category }}</span>
              <span class="text-sm text-gray-500">{{ article.date }}</span>
            </div>
            <h3 class="text-xl font-semibold mb-2">{{ article.title }}</h3>
            <p class="text-gray-600 mb-4">{{ article.excerpt }}</p>
            <div class="flex items-center justify-between">
              <div class="flex items-center text-sm text-gray-500">
                <User class="w-4 h-4 mr-2" :stroke="'#fff'" />
                <span>{{ article.author }}</span>
              </div>
              <router-link :to="`/blog/${article.id}`" class="text-bleumarine hover:text-violet font-medium text-sm">
                Lire la suite →
              </router-link>
            </div>
          </article>
        </div>
        <div v-else class="text-center py-12">
          <p class="text-gray-600 text-lg">Aucun article trouvé correspondant à votre recherche.</p>
        </div>

        <!-- Pagination -->
        <div v-if="filteredArticles.length > 0" class="flex justify-center mt-12">
          <div class="flex space-x-2">
            <button class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50" disabled>
              Précédent
            </button>
            <button class="px-4 py-2 rounded-lg bg-primary-600 text-white">1</button>
            <button class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100">2</button>
            <button class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100">3</button>
            <button class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100">Suivant</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Categories Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="section-title text-center">Catégories</h2>
        <div class="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          <button
            v-for="category in categories"
            :key="category"
            @click="selectedCategory = selectedCategory === category ? '' : category"
            :class="selectedCategory === category ? 'bg-bleumarine text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
            class="px-4 py-2 rounded-full font-medium transition"
          >
            {{ category }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Newspaper, User } from 'lucide-vue-next'
import SearchableSelect from '../components/SearchableSelect.vue'

const searchQuery = ref('')
const selectedCategory = ref('')

const categoryOptions = [
  { value: '', label: 'Toutes les catégories' },
  { value: 'Cyber sécurité', label: 'Cyber sécurité' },
  { value: 'Développement', label: 'Développement' },
  { value: 'Gestion serveur', label: 'Gestion serveur' },
  { value: 'Data Science', label: 'Data Science' },
  { value: 'Data Analyste', label: 'Data Analyste' },
  { value: 'Cloud', label: 'Cloud' },
  { value: 'DevOps', label: 'DevOps' },
  { value: 'Intelligence Artificielle', label: 'Intelligence Artificielle' },
  { value: 'Réseaux', label: 'Réseaux' }
]
const articles = ref([
  {
    id: 1,
    title: 'Les meilleures pratiques de sécurité cloud en 2024',
    excerpt: 'Découvrez comment sécuriser vos infrastructures cloud avec les dernières recommandations de sécurité et protéger vos données sensibles.',
    category: 'Cloud',
    author: 'Kouamé Jean',
    date: '15 Jan 2024'
  },
  {
    id: 2,
    title: 'Introduction à DevOps pour les entreprises',
    excerpt: 'Comprendre les principes DevOps et comment les appliquer dans votre organisation pour améliorer la collaboration et la livraison logicielle.',
    category: 'DevOps',
    author: 'Touré Aminata',
    date: '10 Jan 2024'
  },
  {
    id: 3,
    title: 'Cybersécurité : protéger votre entreprise contre les ransomwares',
    excerpt: 'Guide complet pour se prémunir contre les attaques par ransomware et mettre en place une stratégie de défense efficace.',
    category: 'Cyber sécurité',
    author: 'Diaby Fatou',
    date: '5 Jan 2024'
  },
  {
    id: 4,
    title: 'Les fondamentaux du développement web moderne',
    excerpt: 'Apprenez les bases du développement web avec les technologies actuelles : HTML5, CSS3, JavaScript et les frameworks populaires.',
    category: 'Développement',
    author: 'Koffi Emmanuel',
    date: '28 Déc 2023'
  },
  {
    id: 5,
    title: 'Gestion de serveur Linux pour débutants',
    excerpt: 'Un guide pratique pour administrer vos serveurs Linux : commandes essentielles, gestion des utilisateurs et surveillance système.',
    category: 'Gestion serveur',
    author: 'Kouamé Jean',
    date: '20 Déc 2023'
  },
  {
    id: 6,
    title: 'Introduction à l\'Intelligence Artificielle',
    excerpt: 'Découvrez les concepts fondamentaux de l\'IA, le machine learning et comment ces technologies transforment les entreprises.',
    category: 'Intelligence Artificielle',
    author: 'Touré Aminata',
    date: '15 Déc 2023'
  },
  {
    id: 7,
    title: 'Data Science : analyser et visualiser vos données',
    excerpt: 'Maîtrisez les outils et techniques de data science pour transformer vos données en insights décisionnels pertinents.',
    category: 'Data Science',
    author: 'Diaby Fatou',
    date: '10 Déc 2023'
  },
  {
    id: 8,
    title: 'Architecture réseau : concevoir une infrastructure robuste',
    excerpt: 'Les principes clés pour concevoir et mettre en place une architecture réseau fiable et performante pour votre entreprise.',
    category: 'Réseaux',
    author: 'Koffi Emmanuel',
    date: '5 Déc 2023'
  },
  {
    id: 9,
    title: 'Devenir Data Analyste : compétences et outils',
    excerpt: 'Le parcours pour devenir data analyste : compétences requises, outils à maîtriser et perspectives de carrière.',
    category: 'Data Analyste',
    author: 'Kouamé Jean',
    date: '1 Déc 2023'
  }
])

const filteredArticles = computed(() => {
  return articles.value.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                        article.excerpt.toLowerCase().includes(searchQuery.value.toLowerCase())
    const matchesCategory = selectedCategory.value === '' || article.category === selectedCategory.value
    return matchesSearch && matchesCategory
  })
})

const categories = computed(() => {
  return categoryOptions.filter(opt => opt.value !== '').map(opt => opt.label)
})
</script>
